import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { diffQuote, rebase, newUid } from "@/lib/quoteLive";

const POLL_MS = 1000;
const SAVE_MS = 500;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Aynı teklif birden çok ekranda açıkken: yerel değişiklik ~0.5 sn'de kaydedilir, diğer kullanıcınınki ~1 sn'de ekrana gelir.
// Stok / cari defter etkileri editör kapanınca (finish → commit) uygulanır.
export function useQuoteLive({ id, open, form, setForm, blankItem }) {
  const enabled = !!(open && id);
  const [live, setLive] = useState({ ready: false, status: "idle", editors: [], error: "" });
  const cid = useRef(newUid()).current;   // bu ekranın kimliği (aynı hesapla açılan diğer ekranlar da "birlikte düzenleyen" görünür)
  const R = useRef({ base: null, rev: 0, inflight: false, polling: false, pendingMerge: false, timer: null, form: null, touched: false, closed: false, lastError: "" });

  const merge = useCallback((remote, opsOf) => {
    const s = R.current;
    s.pendingMerge = true;   // ekran yeni hali çizene kadar kaydetme bekler (karşı değişikliği geri almamak için)
    setForm((cur) => rebase(remote, cur, opsOf(cur), blankItem));
  }, [setForm, blankItem]);

  const flush = useCallback(async () => {
    const s = R.current;
    clearTimeout(s.timer);
    s.timer = null;
    if (!s.base || s.inflight) return;
    if (s.pendingMerge) { s.timer = setTimeout(() => flush(), 80); return; }
    const sent = s.form;
    const ops = diffQuote(s.base, sent);
    if (!ops) return;
    s.inflight = true;
    setLive((l) => ({ ...l, status: "saving" }));
    try {
      const { data } = await api.patch(`/quotes-live/${id}`, { base_rev: s.rev, ...ops });
      s.base = data.quote;
      s.rev = data.rev;
      s.touched = true;
      s.lastError = "";
      merge(data.quote, (cur) => diffQuote(sent, cur));
      setLive((l) => ({ ...l, status: "saved", error: "" }));
    } catch (e) {
      s.lastError = apiError(e);
      setLive((l) => ({ ...l, status: "error", error: s.lastError }));
      if (e?.response?.status !== 404) s.timer = setTimeout(() => flush(), 3000);
    } finally { s.inflight = false; }
  }, [id, merge]);

  useEffect(() => {
    const s = R.current;
    s.form = form;
    s.pendingMerge = false;
    if (!enabled || !s.base) return;
    clearTimeout(s.timer);
    s.timer = setTimeout(() => flush(), SAVE_MS);
  }, [form, enabled, flush]);

  useEffect(() => {
    const s = R.current;
    if (!enabled) return undefined;
    let alive = true;
    Object.assign(s, { base: null, rev: 0, touched: false, closed: false, lastError: "" });
    setLive({ ready: false, status: "loading", editors: [], error: "" });
    api.get(`/quotes-live/${id}?cid=${cid}`).then(({ data }) => {
      if (!alive) return;
      s.base = data.quote;
      s.rev = data.rev;
      merge(data.quote, () => null);
      setLive({ ready: true, status: "saved", editors: data.editors || [], error: "" });
    }).catch((e) => { if (alive) setLive({ ready: false, status: "error", editors: [], error: apiError(e) }); });
    const poll = setInterval(async () => {
      if (!s.base || s.inflight || s.polling || document.visibilityState !== "visible") return;
      s.polling = true;
      try {
        const { data } = await api.get(`/quotes-live/${id}?rev=${s.rev}&cid=${cid}`);
        if (!alive) return;
        const eds = data.editors || [];
        setLive((l) => (l.editors.join("|") === eds.join("|") ? l : { ...l, editors: eds }));
        if (data.quote && data.rev > s.rev && !s.inflight) {
          const old = s.base;
          s.base = data.quote;
          s.rev = data.rev;
          merge(data.quote, (cur) => diffQuote(old, cur));
          setLive((l) => ({ ...l, remoteBy: data.quote.updated_by || "", remoteAt: Date.now() }));
        }
      } catch { /* bir sonraki turda tekrar */ }
      finally { s.polling = false; }
    }, POLL_MS);
    return () => {
      alive = false;
      clearInterval(poll);
      // editör "Kapat"/"Kaydet" dışında bir yolla kapandıysa bekleyen değişiklik kaydedilir + etkiler uygulanır
      if (s.base && !s.closed) {
        (async () => {
          await flush();
          if (s.touched) { try { await api.post(`/quotes-live/${id}/commit?cid=${cid}`); s.touched = false; } catch { /* sunucu birkaç dk içinde uygular */ } }
        })();
      }
    };
  }, [enabled, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = useCallback(async () => {
    const s = R.current;
    if (!s.base) return null;
    s.closed = true;
    try {
      for (let n = 0; n < 60 && (s.inflight || s.pendingMerge); n += 1) await wait(100);
      await flush();
      for (let n = 0; n < 60 && s.inflight; n += 1) await wait(100);
      if (s.lastError) throw new Error(s.lastError);
      const { data } = await api.post(`/quotes-live/${id}/commit?cid=${cid}`);
      s.touched = false;
      return data;
    } catch (e) { s.closed = false; throw e; }
  }, [id, flush, cid]);

  return { ...live, finish };
}
